import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3,
  History,
  Package,
  Truck,
  Upload,
  Settings,
  LogOut,
  User,
  Mail,
  FlaskConical,
  RefreshCw,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { api } from '@/api/client';
import { signOut } from '@/lib/auth-client';
import { BUILD_STAMP, GIT_SHA } from '@/lib/build-info';
import { reloadForUpdate } from '@/lib/pwa';

const navigationItems = [
  // Mail first, and the page "/" lands on: single labels and envelope postage
  // are the counter's bread and butter, several times an hour. A bulk run is a
  // once-a-day job, so it does not earn the top slot.
  { title: 'Mail', url: '/mail', icon: Mail },
  { title: 'Upload', url: '/upload', icon: Upload },
  { title: 'Shipping', url: '/shipping', icon: Package },
  { title: 'Reports', url: '/reports', icon: BarChart3 },
  // Admin only: this shows the whole EasyPost account - labels made outside the
  // app, wallet payments - not just what staff did here.
  { title: 'EasyPost', url: '/easypost', icon: Truck, adminOnly: true },
  // Admin only: the trail records what staff did, so it is not theirs to browse.
  { title: 'Activity', url: '/activity', icon: History, adminOnly: true },
  // Admin only too. Everything here is shop configuration — the sender address
  // every label carries, the boxes, the API keys — where a wrong value costs
  // money on every shipment rather than on one.
  { title: 'Settings', url: '/settings', icon: Settings, adminOnly: true },
];

export default function Layout({ children, user }) {
  const location = useLocation();

  /**
   * Re-checked, not fetched once.
   *
   * This used to be staleTime: Infinity — read at load and never again — so a
   * deploy could land and the open window would go on reporting the build it
   * started with. That is half of why nobody was told about a new version.
   */
  const { data: version } = useQuery({
    queryKey: ['version'],
    queryFn: () => api.version(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  // The banner describes the account that will buy the next label, which is the
  // provider chosen in Settings - not always Shippo.
  const activeProviderName = version?.shippingProvider === 'easypost' ? 'EasyPost' : 'Shippo';
  const activeMode =
    version?.shippingProvider === 'easypost' ? version?.easypostMode : version?.shippoMode;

  /**
   * The running bundle is not the build the server is serving.
   *
   * The service worker prompt is the tidy path and it depends on the browser
   * cooperating. This does not: the bundle knows the commit it was built from,
   * the server says which one it is running, and if those disagree then this
   * page is old whatever the cache thinks. It is the difference between asking
   * the mechanism and asking the question.
   *
   * Only when both are known and neither is a dev build, so a local `npm run
   * dev` against a deployed API does not nag.
   */
  const staleBuild =
    Boolean(version?.gitSha) &&
    GIT_SHA !== 'dev' &&
    version.gitSha !== 'dev' &&
    version.gitSha !== GIT_SHA;

  const handleLogout = async () => {
    await signOut();
    window.location.href = '/login';
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-gradient-to-br from-slate-50 to-blue-50">
        <Sidebar className="border-r border-slate-200">
          <SidebarHeader className="border-b border-slate-200 p-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <Package className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-bold text-lg text-slate-900">ShipSwift</h2>
                <p className="text-xs text-slate-500">Label Manager</p>
              </div>
            </div>
          </SidebarHeader>

          <SidebarContent className="p-3">
            <SidebarGroup>
              <SidebarGroupLabel className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-3 py-2">
                Navigation
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {navigationItems
                    .filter((item) => !item.adminOnly || user?.role === 'admin')
                    .map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        className={`hover:bg-blue-50 hover:text-blue-700 transition-all duration-200 rounded-lg mb-1 ${
                          location.pathname === item.url ? 'bg-blue-100 text-blue-700 shadow-sm' : ''
                        }`}
                      >
                        <Link to={item.url} className="flex items-center gap-3 px-3 py-2.5">
                          <item.icon className="w-5 h-5" />
                          <span className="font-medium">{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter className="border-t border-slate-200 p-4">
            <div className="space-y-3">
              {/* Impossible to miss, on purpose: in test mode nothing bought
                  here is a real label, and a shipment that never ships is worse
                  than an obvious banner.

                  Driven by the Shippo key in use rather than a separate
                  setting. The two used to be able to disagree, and did — this
                  read TEST MODE while a live key was buying real postage, which
                  is the one direction a money indicator must never be wrong
                  in. */}
              {activeMode === 'test' && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-100 border border-amber-300">
                  <FlaskConical className="w-4 h-4 text-amber-700 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-amber-900">TEST MODE</p>
                    <p className="text-[11px] text-amber-700 leading-tight">
                      Labels are not real postage
                    </p>
                  </div>
                </div>
              )}

              {staleBuild && (
                <button
                  type="button"
                  onClick={reloadForUpdate}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-100 border border-blue-300 text-left hover:bg-blue-200 transition-colors"
                >
                  <RefreshCw className="w-4 h-4 text-blue-700 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-blue-900">UPDATE READY</p>
                    <p className="text-[11px] text-blue-700 leading-tight">
                      Click to load the new version
                    </p>
                  </div>
                </button>
              )}

              {/* An unrecognised key could be either. Say so rather than pick
                  the reassuring answer. */}
              {activeMode === 'unknown' && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 border border-slate-300">
                  <FlaskConical className="w-4 h-4 text-slate-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-800">MODE UNKNOWN</p>
                    <p className="text-[11px] text-slate-600 leading-tight">
                      Check the {activeProviderName} key in Settings
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shrink-0">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">
                    {user?.name ?? 'User'}
                  </p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </div>
              </div>

              <Button
                onClick={handleLogout}
                variant="outline"
                className="w-full justify-start gap-2 hover:bg-red-50 hover:text-red-700 hover:border-red-200 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Sign out</span>
              </Button>

              <p
                className={`text-[11px] text-center ${staleBuild ? 'text-blue-600' : 'text-slate-400'}`}
                title={
                  version
                    ? `This page: ${BUILD_STAMP}
Server: v${version.version} · ${version.gitSha}`
                    : undefined
                }
              >
                {BUILD_STAMP}
                {staleBuild && ` → ${version.gitSha}`}
              </p>
            </div>
          </SidebarFooter>
        </Sidebar>

        <main className="flex-1 flex flex-col min-w-0">
          <header className="bg-white/70 backdrop-blur-md border-b border-slate-200 px-6 py-4 md:hidden sticky top-0 z-10">
            <div className="flex items-center gap-4">
              <SidebarTrigger className="hover:bg-slate-100 p-2 rounded-lg transition-colors duration-200" />
              <h1 className="text-xl font-semibold">ShipSwift</h1>
            </div>
          </header>

          <div className="flex-1 overflow-auto">{children}</div>
        </main>
      </div>
    </SidebarProvider>
  );
}
